import {Box, Flex, P} from 'exsportia-components'
import React, {FC, ReactNode} from 'react'

import {
  MembershipCancelRequest,
  MembershipCancelRequestDecision,
  MembershipCancelRequestStatusEnum,
} from '../../../api/subscription-bookings/types'
import {TranslatorScope} from '../../../locale/types'
import {Button} from '../../../ui/components/button/button'
import {useLocale} from '../../../utils/i18n'

type LFunc = (key: TranslatorScope, options?: Record<string, unknown>) => string

// Minor units to a readable amount, for example 150000 THB satang -> "฿1,500".
export const formatMembershipRefund = (l: LFunc, minorAmount: number, currency: string) => {
  const major = (minorAmount / 100).toLocaleString('en-US', {maximumFractionDigits: 2})
  return l(`${currency}_symbolic` as TranslatorScope, {value: major})
}

// What the venue decided, as the member reads it. A decision never moves money: refunds are
// a separate step that the venue does on its own.
export const getAcceptedDecisionText = (l: LFunc, decision: MembershipCancelRequestDecision | null) => {
  if (!decision || decision.kind === 'full') {
    return l('membershipCancel_acceptedFull')
  }
  const amount = formatMembershipRefund(l, decision.refundAmount, decision.currency)
  return decision.paidWith
    ? l('membershipCancel_acceptedPartialPaidWith', {amount, paidWith: decision.paidWith})
    : l('membershipCancel_acceptedPartial', {amount})
}

type Props = {
  request: MembershipCancelRequest
  isWithdrawing?: boolean
  onWithdraw: () => void
}

export const MembershipCancelStatusCard: FC<Props> = ({request, isWithdrawing = false, onWithdraw}) => {
  const {l} = useLocale()
  const isOpen = request.status === MembershipCancelRequestStatusEnum.REQUESTED

  const statusLabels: Record<MembershipCancelRequestStatusEnum, string> = {
    [MembershipCancelRequestStatusEnum.REQUESTED]: l('membershipCancel_statusRequestedLabel'),
    [MembershipCancelRequestStatusEnum.ACCEPTED]: l('membershipCancel_statusAcceptedLabel'),
    [MembershipCancelRequestStatusEnum.DECLINED]: l('membershipCancel_statusDeclinedLabel'),
    [MembershipCancelRequestStatusEnum.WITHDRAWN]: l('membershipCancel_statusWithdrawnLabel'),
  }

  let detail: ReactNode = null
  switch (request.status) {
    case MembershipCancelRequestStatusEnum.REQUESTED:
      detail = <P fontSize={'body'}>{l('membershipCancel_statusRequested')}</P>
      break
    case MembershipCancelRequestStatusEnum.ACCEPTED:
      detail = (
        <>
          <P
            fontSize={'body'}
            data-testid='membership-cancel-decision'
          >
            {getAcceptedDecisionText(l, request.decision)}
          </P>
          {request.decision?.terms && (
            <P fontSize={'body'}>{l('membershipCancel_venueTerms', {terms: request.decision.terms})}</P>
          )}
        </>
      )
      break
    case MembershipCancelRequestStatusEnum.DECLINED:
      detail = (
        <>
          <P fontSize={'body'}>{l('membershipCancel_statusDeclined')}</P>
          {request.declineMessage && (
            <P
              fontSize={'body'}
              data-testid='membership-cancel-decline-message'
            >
              {l('membershipCancel_declinedMessage', {message: request.declineMessage})}
            </P>
          )}
        </>
      )
      break
    case MembershipCancelRequestStatusEnum.WITHDRAWN:
      detail = <P fontSize={'body'}>{l('membershipCancel_statusWithdrawn')}</P>
      break
  }

  return (
    <Box
      data-testid='membership-cancel-status'
      data-status={request.status}
      p={'16px'}
      borderRadius={'12px'}
      border={'1px solid'}
      borderColor={'border'}
    >
      <P
        fontWeight={'bold'}
        fontSize={'h4'}
      >
        {l('membershipCancel_statusTitle')}
      </P>
      <P
        fontWeight={'bold'}
        mt={'8px'}
        data-testid='membership-cancel-status-label'
      >
        {statusLabels[request.status]}
      </P>
      <Box mt={'8px'}>{detail}</Box>
      {isOpen && (
        <Flex mt={'12px'}>
          <Button
            type='secondary'
            onClick={onWithdraw}
            isLoading={isWithdrawing}
          >
            {l('membershipCancel_withdraw')}
          </Button>
        </Flex>
      )}
    </Box>
  )
}
